<template>
  <!-- Full-screen modal overlay -->
  <div
    v-if="isOpen"
    class="checklist-image-modal"
  >
    <!-- Close button -->
    <button
      class="close-button"
      @click="handleClose"
      aria-label="Close"
    >
      <svg class="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
    </button>

    <!-- Previous button -->
    <button
      v-if="images.length > 1"
      class="nav-button nav-button-left"
      :class="{ disabled: currentImageIndex === 0 }"
      :disabled="currentImageIndex === 0"
      @click="goToPrevious"
      aria-label="Previous image"
    >
      <svg class="icon-nav" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>

    <!-- Loading State -->
    <div v-if="isLoading" class="content-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading information...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="content-container">
      <svg class="icon-error" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
      <p class="error-text">{{ error }}</p>
      <button class="retry-button" @click="fetchImages" aria-label="Retry loading images">
        <svg class="icon-retry" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23 4 23 10 17 10"></polyline>
          <polyline points="1 20 1 14 7 14"></polyline>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
        </svg>
        Retry
      </button>
    </div>

    <!-- Image Display -->
    <div v-else-if="images.length > 0" class="image-container">
      <!-- 画像読み込み中のローディング表示 -->
      <div v-if="isImageLoading && !imageLoadError" class="content-container image-loading-overlay">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading image...</p>
      </div>
      
      <!-- 画像読み込みエラー表示 -->
      <div v-else-if="imageLoadError" class="content-container">
        <svg class="icon-image-error" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
        <p class="error-text">Image failed to load</p>
        <button class="retry-button" @click="fetchImages" aria-label="Retry loading images">
          <svg class="icon-retry" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          Retry
        </button>
      </div>
      
      <!-- 画像要素（読み込み中は非表示） -->
      <img
        v-show="!isImageLoading && !imageLoadError"
        :src="images[currentImageIndex].url"
        :alt="`Checklist image ${currentImageIndex + 1}`"
        class="image"
        decoding="async"
        fetchpriority="high"
        @load="handleImageLoad"
        @error="handleImageError"
      />
    </div>

    <!-- No Images State -->
    <div v-else class="content-container">
      <svg class="icon-no-image" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        <circle cx="8.5" cy="8.5" r="1.5"></circle>
        <polyline points="21 15 16 10 5 21"></polyline>
      </svg>
      <p class="no-image-text">No images available</p>
    </div>

    <!-- Next button -->
    <button
      v-if="images.length > 1"
      class="nav-button nav-button-right"
      :class="{ disabled: currentImageIndex === images.length - 1 }"
      :disabled="currentImageIndex === images.length - 1"
      @click="goToNext"
      aria-label="Next image"
    >
      <svg class="icon-nav" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>

    <!-- Bottom info panel -->
    <div v-if="!isLoading && !error" class="info-panel">
      <!-- VIN and Model Name -->
      <div class="info-box">
        <div class="info-grid">
          <div class="info-label info-label-vin">VIN</div>
          <div class="info-colon">:</div>
          <div class="info-value info-value-vin">{{ row?.vin || 'N/A' }}</div>
          
          <div class="info-label info-label-model">Model Name</div>
          <div class="info-colon">:</div>
          <div class="info-value info-value-model">{{ row?.modelName || 'N/A' }}</div>
        </div>
      </div>

      <!-- Image counter -->
      <div v-if="images.length > 1" class="counter-box">
        <span class="counter-text">{{ currentImageIndex + 1 }} / {{ images.length }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import McusDataService from "../services/McusDataService";

const lockBodyScroll = () => {
  // 背景（一覧画面）のスクロールを止める
  document.body.style.overflow = "hidden";
};

const unlockBodyScroll = () => {
  document.body.style.overflow = "";
};

defineOptions({ name: "ChecklistImageModal" });

const store = useStore();

const props = defineProps({
  row: {
    type: Object,
    default: null,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const images = ref([]);
const currentImageIndex = ref(0);
const isLoading = ref(false);
const error = ref("");
const imageLoadError = ref(false);
const isImageLoading = ref(false); // 個別画像の読み込み状態
const fetchSeq = ref(0); // 非同期fetchの競合対策（呼び出し世代）

/**
 * 画像の体感表示速度改善のためのプリロード（先読み）
 * - ブラウザキャッシュに乗せて、次/前の切り替えを速くする
 * - 同時実行数を制限してネットワークを圧迫しない
 */
const preloadConcurrency = 2;
const preloadedUrls = new Set();
const inFlightPreloads = new Map(); // url -> Promise<void>

const preloadOne = (url) => {
  if (!url || preloadedUrls.has(url)) return Promise.resolve();
  if (inFlightPreloads.has(url)) return inFlightPreloads.get(url);

  const p = new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    // fetchPriority は一部ブラウザのみ対応（未対応でも問題なし）
    try {
      img.fetchPriority = "low";
    } catch (_) {
      // no-op
    }

    img.onload = () => {
      preloadedUrls.add(url);
      inFlightPreloads.delete(url);
      resolve();
    };
    img.onerror = () => {
      // 壊れたURL等で無限リトライにならないよう「試行済み」として扱う
      preloadedUrls.add(url);
      inFlightPreloads.delete(url);
      resolve();
    };
    img.src = url;
  });

  inFlightPreloads.set(url, p);
  return p;
};

const preloadImagesAroundIndex = (index) => {
  // 現在画像の前後 + 次の次 まで先読み（必要に応じて調整）
  const urls = [
    images.value?.[index - 1]?.url,
    images.value?.[index + 1]?.url,
    images.value?.[index + 2]?.url,
  ].filter(Boolean);

  let cursor = 0;
  const workers = Array.from({ length: preloadConcurrency }, async () => {
    while (cursor < urls.length) {
      const url = urls[cursor++];
      await preloadOne(url);
    }
  });

  void Promise.all(workers);
};

/**
 * Fetch images from API.
 * Called automatically when modal opens.
 */
const fetchImages = async () => {
  const mySeq = ++fetchSeq.value;

  if (!props.row?.checklistImage) {
    error.value = "No checklist image URL found";
    return;
  }

  isLoading.value = true;
  error.value = "";
  images.value = [];
  currentImageIndex.value = 0;
  imageLoadError.value = false;

  try {
    const response = await McusDataService.getChecklistImages({
      checklistImage: props.row.checklistImage,
      log: store.state.logData,
    });

    if (mySeq !== fetchSeq.value) return;

    if (response.status === 200 && response.data?.images) {
      images.value = response.data.images;

      if (images.value.length === 0) {
        error.value = "No images available for this checklist";
      } else {
        // 最初の画像の読み込みを開始
        isImageLoading.value = true;

        // 初回表示とナビゲーションの体感改善：周辺画像をプリロード
        preloadImagesAroundIndex(0);
      }
    } else {
      error.value = "Failed to load images";
    }
  } catch (err) {
    if (mySeq !== fetchSeq.value) return;

    console.error("Error fetching checklist images:", err);
    const status = err?.response?.status;

    if (status === 400) {
      error.value = "Invalid request. Please try again.";
    } else if (status === 401 || status === 403) {
      error.value = "Authentication failed. Please login again.";
    } else if (status >= 500) {
      error.value = "Server error. Please try again later.";
    } else {
      error.value = "Failed to load images. Please try again.";
    }
  } finally {
    // eslint(no-unsafe-finally): finally内でreturnすると例外/returnを上書きし得るため、ガードはreturnせず条件分岐にする
    if (mySeq === fetchSeq.value) {
      isLoading.value = false;
    }
  }
};

/** Navigate to previous image */
const goToPrevious = () => {
  if (currentImageIndex.value > 0) {
    isImageLoading.value = true; // ローディング状態を設定
    currentImageIndex.value--;
    imageLoadError.value = false;

    preloadImagesAroundIndex(currentImageIndex.value);
  }
};

/** Navigate to next image */
const goToNext = () => {
  if (currentImageIndex.value < images.value.length - 1) {
    isImageLoading.value = true; // ローディング状態を設定
    currentImageIndex.value++;
    imageLoadError.value = false;

    preloadImagesAroundIndex(currentImageIndex.value);
  }
};

/** Handle image load complete */
const handleImageLoad = () => {
  isImageLoading.value = false; // ローディング完了
};

/** Handle image load error */
const handleImageError = () => {
  isImageLoading.value = false; // ローディング状態を解除
  imageLoadError.value = true;
};

/** Close modal */
const handleClose = () => {
  emit("close");
};

/** Handle keyboard navigation (Arrow keys, Escape) */
const handleKeyDown = (e) => {
  if (!props.isOpen) return;

  if (e.key === "Escape") {
    handleClose();
  } else if (e.key === "ArrowLeft") {
    goToPrevious();
  } else if (e.key === "ArrowRight") {
    goToNext();
  }
};

/** Watch for modal open/close to fetch images or reset state */
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      lockBodyScroll();
      fetchImages();
    } else {
      unlockBodyScroll();
      // close時点で世代を進め、進行中fetchの結果を無効化する
      fetchSeq.value++;

      // Reset state when closing
      images.value = [];
      currentImageIndex.value = 0;
      error.value = "";
      imageLoadError.value = false;
      isImageLoading.value = false;
      isLoading.value = false;

      // モーダル終了時はプリロード管理情報もクリア
      preloadedUrls.clear();
      inFlightPreloads.clear();
    }
  }
);

/** Add keyboard event listener on mount */
onMounted(() => {
  window.addEventListener("keydown", handleKeyDown);
  if (props.isOpen) lockBodyScroll();
});

/** Remove keyboard event listener on unmount */
onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
  unlockBodyScroll();
});
</script>

<style scoped>
.checklist-image-modal {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: white;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  z-index: 10000;
  padding: 0;
}

.close-button:hover {
  color: #d1d5db;
}

.icon-close {
  width: 2rem;
  height: 2rem;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  z-index: 10000;
  padding: 0;
}

.nav-button:hover:not(.disabled) {
  color: #d1d5db;
}

.nav-button.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-button-left {
  left: 1rem;
}

.nav-button-right {
  right: 1rem;
}

.icon-nav {
  width: 3rem;
  height: 3rem;
}

.content-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 90vw;
  max-height: 90vh;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: white;
  font-size: 1.125rem;
  margin-top: 1rem;
}

.icon-error,
.icon-image-error,
.icon-no-image {
  width: 6rem;
  height: 6rem;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.error-text {
  color: white;
  font-size: 1.125rem;
  margin-bottom: 1rem;
}

.retry-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: rgba(59, 130, 246, 0.8);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.retry-button:hover {
  background-color: rgba(59, 130, 246, 1);
  transform: scale(1.05);
}

.retry-button:active {
  transform: scale(0.98);
}

.icon-retry {
  width: 1.25rem;
  height: 1.25rem;
}

.no-image-text {
  color: white;
  font-size: 1.125rem;
}

.image-container {
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.image-loading-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.info-panel {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  z-index: 10000;
}

.info-box {
  background-color: rgba(0, 0, 0, 0.5);
  color: #d1d5db;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
}

.info-grid {
  display: grid;
  grid-template-columns: auto auto auto;
  column-gap: 0.5rem;
  row-gap: 0.25rem;
  font-size: 1rem;
}

.info-label {
  text-align: left;
}

.info-colon {
  text-align: left;
}

.info-value {
  text-align: left;
}

.counter-box {
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
}

.counter-text {
  font-size: 0.875rem;
  font-weight: 500;
}

@media (max-width: 768px) {
  .icon-close {
    width: 1.5rem;
    height: 1.5rem;
  }

  .icon-nav {
    width: 2rem;
    height: 2rem;
  }

  .info-grid {
    font-size: 0.875rem;
  }

  .counter-text {
    font-size: 0.75rem;
  }

  .retry-button {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
  }

  .icon-retry {
    width: 1rem;
    height: 1rem;
  }

  .error-text {
    font-size: 1rem;
  }
}
</style>
